<script>
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { backOut } from "svelte/easing";
  import {
    surveyStore,
    currentStep,
    resetSurvey,
    calculateRecommendation,
    formatRecommendation,
    recommendationStore,
    getElevatedClub,
    getElevatedClubUrl
  } from "../../stores/surveyStore";
  import { getRecommendation } from "../../services/api";
  import LoadingAnimation from "../LoadingAnimation.svelte";
  import { getConfig } from "../../config.js";

  // Get configuration including stopMembershipSelling flag
  const config = getConfig();

  // Direct image imports for different seating types
  // Loudville images
  import loudville1Img from "../../assets/images/loudville/loudville_1.webp";
  import loudville2Img from "../../assets/images/loudville/loudville_2.webp";
  import loudville3Img from "../../assets/images/loudville/loudville_3.webp";

  // Club images
  import club1Img from "../../assets/images/club/club_1.webp";
  import club2Img from "../../assets/images/club/club_2.webp";
  import club3Img from "../../assets/images/club/club_3.webp";

  // Lower level images
  import lower1Img from "../../assets/images/lower/lower_1.webp";
  import lower2Img from "../../assets/images/lower/lower_2.webp";
  import lower3Img from "../../assets/images/lower/lower_3.webp";

  // Premium images
  import chairmansImg from "../../assets/images/premium/chairmans.jpg";
  import championsImg from "../../assets/images/premium/champions.jpg";
  import signatureImg from "../../assets/images/premium/signature.jpg";

  // Benefit/perk images
  import concertsImg from "../../assets/images/benefits/concerts.png";
  import loungeImg from "../../assets/images/benefits/louge.png";
  import membershipEventsImg from "../../assets/images/benefits/membership_events.jpg";
  import playoffAccessImg from "../../assets/images/benefits/playoff_access.jpg";
  import teamShopImg from "../../assets/images/benefits/team_shop.png";
  import ticketExchangeImg from "../../assets/images/benefits/ticket_exchange.png";
  import membershipServiceRepImg from "../../assets/images/benefits/membership_service_rep.jpg";

  // Fallback images
  import arena1Img from "../../assets/images/arena1.jpg";
  import arena2Img from "../../assets/images/arena2.png";
  import fans1Img from "../../assets/images/fans1.jpg";

  // Recommendation header logos: Cavs United shield for Quarter/Half/Full,
  // Foundation mark for Premium (Foundation) recommendations.
  import unitedLogo from "../../assets/images/logos/united-logo.png";
  import foundationLogo from "../../assets/images/logos/foundation-logo.png";

  let loading = true;
  // let recommendation = null;
  $: recommendation = $recommendationStore;

  const attributionSuffix = "&aid=15870&pid=web&rid=25261&utm_source=cavs&utm_medium=web&utm_campaign=25261memberships&utm_content=recommendationtool";

  function appendAttribution(url, suffix) {
    if (!url || typeof url !== 'string') return url; // Basic safety check
    const cleanSuffix = suffix.startsWith('&') ? suffix : '&' + suffix;
    if (url.includes('?')) {
      return url + cleanSuffix;
    } else {
      return url + '?' + cleanSuffix.substring(1);
    }
  }
  let error = null;
  const LOADING_DURATION = 3000;

  // Toggle for showing old pricing
  let showDiscountPricing = Date.now() < getEarlyBirdEndDate().getTime(); // set to false to hide old pricing

  // ===============================
  // EARLY BIRD COUNTDOWN
  // ===============================
  let countdownText = "";
  let showEarlyBirdCountdown = false;
  let countdownInterval = null;

  // March 1, 2026 @ 11:59:59 PM ET
  function getEarlyBirdEndDate() {
    // Date.UTC(year, monthIndex, day, hour, minute, second)
    return new Date(Date.UTC(2026, 2, 2, 4, 59, 59));
  }

  function updateCountdown() {
    const diff = getEarlyBirdEndDate().getTime() - Date.now();

    if (diff <= 0) {
      showEarlyBirdCountdown = false;
      clearInterval(countdownInterval);
      countdownInterval = null;
      return;
    }

    showEarlyBirdCountdown = true;

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    countdownText = `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  // Consolidated SeatGeek URL for all memberships when selling is stopped
  const consolidatedMembershipUrl = appendAttribution("https://seatgeek.com/cleveland-cavaliers-tickets/ticket-packages", attributionSuffix);
  
  // Define URLs for different recommendation types  
  const standardRepresentativeUrl = appendAttribution("https://outlook.office365.com/book/CavaliersTicketSales@Clecavs.onmicrosoft.com/s/8U5lte4K0k2j-IJyBIMZMw2", attributionSuffix);
  const premiumRepresentativeUrl = appendAttribution("https://outlook.office365.com/book/CavaliersTicketSales@Clecavs.onmicrosoft.com/s/NaahNSoxR0WoyLu3TF8Qyg2", attributionSuffix);
  const premiumClubsUrl = config.stopMembershipSelling 
    ? appendAttribution("https://seatgeek.com/cleveland-cavaliers-tickets/ticket-packages", attributionSuffix)
    : appendAttribution("https://www.rocketarena.com/premium", attributionSuffix);
  // Quarter / Half / Full all route to the SeatGeek ticket-packages page
  const halfMembershipUrl = config.ticketPackagesUrl;
  const fullMembershipUrl = config.ticketPackagesUrl;

  // Pre-calculate frequently used attributed URLs
  $: attributedFaqUrl = config.ticketPackagesUrl;
  $: attributedBenefitsUrl = appendAttribution("https://www.nba.com/cavaliers/tickets/membership-benefits", attributionSuffix);

  // Use a computed property to determine which URL to use
  $: representativeBookingUrl = packageType === "Premium" ? premiumRepresentativeUrl : standardRepresentativeUrl;

    // Add this function to detect mobile devices based on screen width
  function isMobileDevice() {
    // This will run in the browser environment only
    if (typeof window === 'undefined') return false;
    
    // Common mobile breakpoint is 768px
    return window.innerWidth <= 768;
  }
  
  // Call button state
  let showCallButton = false;
  let callButtonTimer;
  
  // Then modify the existing call button visibility logic to include mobile check
  function updateCallButtonState() {
    // Show call button on mobile devices only (no time restrictions)
    showCallButton = isMobileDevice();
  }

  /**
   * Get the appropriate package link based on recommendation
   * @param {string|object} recommendationCode - The recommendation code or package name object
   * @returns {string} Link to the package or contact page
   */
  function getPackageLink(recommendationCode) {
    // Handle undefined or null input
    if (!recommendationCode) {
      console.error("No recommendation code provided to getPackageLink");
      return "https://seatgeek.com/cleveland/series/316ac9b6-64b9-ef11-84e1-d488957632f3?performer_id=2094&step=seats&sgo_ac="; // Default to Quarter
    }

    // Extract just the package type (Quarter, Half, Full, Premium)
    let packageType = "";
    let level = "";

    // Handle different formats
    if (typeof recommendationCode === "string") {
      // Extract from string (Quarter / Lower, Half / Club, etc.)
      const parts = recommendationCode.split(/[\s\/]/);
      packageType = parts[0]; // Quarter, Half, Full, Premium
      if (parts.length > 1) {
        level = parts[parts.length - 1]; // Lower, Club, Loudville
      }
    } else if (recommendationCode && recommendationCode.packageName) {
      // Handle when passing the full recommendation object
      const parts = recommendationCode.packageName.split(/[\s\/]/);
      packageType = parts[0]; // Quarter, Half, Full, Premium
      if (parts.length > 1) {
        level = parts[parts.length - 1].replace("Plan", "").trim(); // Lower, Club, Loudville
      }
    }

    console.log("Extracted package type:", packageType);
    console.log("Extracted level:", level);

    // For Full and Premium packages, return the representative booking URL
    if (packageType === "Full" || packageType === "Premium") {
      // Use representative booking URL based on package
      return packageType === "Premium" ? premiumRepresentativeUrl : standardRepresentativeUrl;
    }


    // For other packages, return the appropriate SeatGeek URL
    const packageLinks = {
      Quarter:
        "https://www.cavs.com/enroll",
      Half: "https://www.cavs.com/enroll",
    };

    return packageLinks[packageType] || packageLinks["Quarter"]; // Default to Quarter if not found
  }

  /**
   * Get the appropriate images based on seating level
   * @param {string} level - The seating level (Lower, Club, Loudville, Premium)
   * @returns {Array} Array of image URLs
   */
  function getSeatingImages(level) {
    if (!level) {
      // Default to general arena images if no level specified
      return [arena1Img, arena2Img, fans1Img];
    }

    // Normalize level name to handle different formats
    level = level.toLowerCase().trim();
    console.log('sastelevel');
    console.log(level);

    if (level.includes("lower")) {
      return [lower1Img, lower2Img, lower3Img];
    } else if (level.includes("club")) {
      return [club1Img, club2Img, club3Img];
    } else if (level.includes("loudville")) {
      return [loudville1Img, loudville2Img, loudville3Img];
    } else if (level.includes("premium")) {
      return [chairmansImg, championsImg, signatureImg];
    }

    // Default fallback
    return [arena1Img, arena2Img, fans1Img];
  }

  /**
   * Check if the package requires contacting a representative instead of direct purchase
   * @param {string} packageType - The package type (Quarter, Half, Full, Premium)
   * @returns {boolean} True if contact representative is required
   */
  function isContactRepPackage(packageType) {
    return packageType === "Full" || packageType === "Premium";
  }

  /**
   * Get the summary text based on the recommendation
   * @param {Object} recommendation - The recommendation object
   * @returns {string} Summary text
   */
  function getSummaryText(recommendation) {
    if (!recommendation || !recommendation.packageName) return "";

    const packageType = recommendation.packageName.split(/[\s\/]/)[0]; // Quarter, Half, Full, Premium
    const level = recommendation.packageName
      .replace(packageType, "")
      .replace("Plan", "")
      .trim();

    // Summary templates based on package and level
    const summaries = {
      Quarter: {
        Loudville:
          "Perfect for new fans - enjoy <b>11</b> Cavs games from our upper level with great views of the entire court!",
        Club: "Enjoy <b>11</b> action-packed Cavs games from our comfortable club level with premium amenities.",
        Lower:
          "Experience <b>11</b> Cavs games from the lower level for an up-close view of all the excitement!",
        default:
          "Gain access to <b>11</b> Cavs games (<b>1</b> preseason, <b>10</b> regular season) and save <b>12%</b> over single-game pricing!",
      },
      Half: {
        Loudville:
          "Step up your fandom with <b>21</b> games from our upper level with fantastic arena views and energy!",
        Club: "Experience <b>21</b> great Cavs moments from our club level with enhanced comfort and amenities!",
        Lower:
          "Get closer to the action with <b>21</b> games in the lower bowl for an immersive gameday experience!",
        default:
          "Elevate your fan experience with <b>21</b> Cavs games (<b>1</b> preseason, <b>20</b> regular season) and save <b>16%</b> over single-game pricing!",
      },
      Full: {
        Loudville:
          "Become a true Cavs fan with all <b>43</b> home games from our energetic upper level seats!",
        Club: "Never miss a moment with all <b>43</b> home games from our club level with premium amenities!",
        Lower:
          "Experience every play with all <b>43</b> home games from our lower level for the ultimate fan experience!",
        default:
          "Get the complete Cavs experience with all <b>43</b> home games (<b>2</b> preseason, <b>41</b> regular season) and save <b>25%</b> over single-game pricing!",
      },
      Premium: {
        default:
          "Foundation is our premium membership, offering access to the finest seats and exclusive club spaces at Rocket Arena and elevated experiences throughout the season and beyond.",
      },
    };

    // Get the appropriate summary based on package type and level
    return (
      (summaries[packageType] && summaries[packageType][level]) ||
      summaries[packageType]?.default ||
      "Your personalized Cavs United membership recommendation based on your preferences!"
    );
  }

  
  /**
   * Get the user's selected perks from the survey
   * @returns {Array} Array of selected perk IDs
   */
  function getSelectedPerks() {
    // Check all possible keys where perks might be stored
    const perksData =
      $surveyStore.perks ||
      $surveyStore.q7 ||
      $surveyStore.q10 ||
      $surveyStore["multi-choice-7"] ||
      $surveyStore["multi-select-7"] ||
      $surveyStore["multi-choice-10"] ||
      $surveyStore["multi-select-10"];

    if (!perksData) return [];

    // Handle different data formats
    if (Array.isArray(perksData)) {
      return perksData; // Already in the format we need
    } else if (typeof perksData === "object") {
      // Convert object format with boolean values to array of selected IDs
      return Object.entries(perksData)
        .filter(
          ([_, selected]) =>
            selected === true || selected === 1 || selected === "1",
        )
        .map(([id]) => id);
    } else if (typeof perksData === "string") {
      // Single selection as string
      return [perksData];
    }

    return [];
  }


    
  /**
   * Get perk image and label based on perk ID
   * @param {string} perkId - The perk ID
   * @returns {Object} Object with image URL and label
   */
  function getPerkInfo(perkId) {
    // Map perk IDs to images and labels
    const perkMap = {
      a: {
        image: teamShopImg,
        label: "Discounts on merchandise and concessions",
      },
      b: {
        image: membershipEventsImg,
        label: "Exclusive events and experiences",
      },
      c: {
        image: playoffAccessImg,
        label: "Priority access to playoff tickets",
      },
      d: {
        image: membershipServiceRepImg, // No specific image for member specialist
        label: "Dedicated member specialist",
      },
      e: {
        image: ticketExchangeImg,
        label: "No fees on additional tickets",
      },
      f: {
        image: concertsImg,
        label: "Priority access to concerts and other Rocket Arena events",
      },
      g: {
        image: ticketExchangeImg,
        label: "Flexibility to exchange games for others",
      },
      pp: { // New perk for Payment Plans
        image: membershipServiceRepImg, // Placeholder image
        label: "Flexible Payment Plans",
      },
    };

    return perkMap[perkId] || { image: arena1Img, label: "Member benefit" };
  }

  // Format phone number for display
  function formatPhoneNumber(phoneNumber) {
    // Remove any non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Format for display (e.g., (216) 555-1234)
    let display;
    if (cleaned.length === 10) {
      display = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      display = `1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
    } else {
      display = phoneNumber; // Return original if invalid format
    }
    
    // Format for dialing (tel: protocol)
    const dialing = `tel:${cleaned}`;
    
    return { display, dialing };
  }

  // Phone number to use for the call button
  const phoneNumber = "2164202050"; // Cavs main phone number
  $: formattedPhone = formatPhoneNumber(phoneNumber);

  // Handle phone call tracking
  function handlePhoneCallClick(event) {
    // Phone call click handler
  }
  
  // Updated function to organize perks:
  // 1. Ticket exchange perk (user selected 'e' or 'g', or default 'g')
  // 2. Two additional perks:
  //    - User's other selected perks (if any, up to 2)
  //    - Default to 'b' (Events) and 'pp' (Payment Plans) if slots are still open
  function organizePerksWithTicketExchangeFirst(userSelectedPerksRaw) {
  // Handle cases where userSelectedPerksRaw is null, undefined, or empty
  const userSelectedPerks = userSelectedPerksRaw && Array.isArray(userSelectedPerksRaw) && userSelectedPerksRaw.length > 0 
    ? [...userSelectedPerksRaw] 
    : [];
  
  let ticketExchangePerk;
  
  // 1. Determine the ticket exchange perk
  if (userSelectedPerks.includes('e')) {
    ticketExchangePerk = 'e';
  } else if (userSelectedPerks.includes('g')) {
    ticketExchangePerk = 'g';
  } else {
    ticketExchangePerk = 'g'; // Default ticket exchange perk
  }

  // 2. Start building the final perks list with the ticket exchange perk
  let finalPerks = [ticketExchangePerk];
  
  // 3. Gather other perks selected by the user (excluding the chosen ticket exchange perk)
  const otherUserSelectedPerks = userSelectedPerks.filter(p => p !== ticketExchangePerk);
  
  // 4. Add up to 2 user-selected other perks
  for (let i = 0; i < Math.min(otherUserSelectedPerks.length, 2); i++) {
    finalPerks.push(otherUserSelectedPerks[i]);
  }
  
  // 5. If we need more perks, fill from defaults
  // Array of all possible default perks, prioritized
  const allPossibleDefaults = ['b', 'pp', 'a', 'c', 'd', 'f']; 
  
  let i = 0;
  while (finalPerks.length < 3 && i < allPossibleDefaults.length) {
    const defaultPerk = allPossibleDefaults[i];
    // Only add if not already included
    if (!finalPerks.includes(defaultPerk)) {
      finalPerks.push(defaultPerk);
    }
    i++;
  }
  
  // 6. Ensure we have exactly 3 perks
  return finalPerks.slice(0, 3);
}

  let resizeHandler;

  onMount(() => {
    console.log("ResultsPage mounted", $surveyStore);

    setTimeout(() => {
      try {
        const recommendationCode = calculateRecommendation($surveyStore);
        if (!recommendationCode || !recommendation) {
          throw new Error("Recommendation failed");
        }
        loading = false;
      } catch (err) {
        error = err.message;
        loading = false;
      }
    }, LOADING_DURATION);

    // Call button logic
    updateCallButtonState();
    callButtonTimer = setInterval(updateCallButtonState, 60000);

    resizeHandler = () => updateCallButtonState();
    window.addEventListener("resize", resizeHandler);

    // ===============================
    // START EARLY BIRD COUNTDOWN
    // ===============================
    updateCountdown();
    countdownInterval = setInterval(updateCountdown, 1000);

    // ===============================
    // CLEANUP
    // ===============================
    return () => {
      if (callButtonTimer) clearInterval(callButtonTimer);
      if (countdownInterval) clearInterval(countdownInterval);
      if (resizeHandler) window.removeEventListener("resize", resizeHandler);
    };
  });

  // // Function to handle retaking the survey
  function handleRetakeSurvey() {
    // Reset the survey data and go back to the beginning
    resetSurvey();
  }

  function handleReserveClick(event) {
  // Prevent the default navigation
  event.preventDefault();

  const targetHref = event.currentTarget.href;

  // Open in a new tab instead of changing the current location
  setTimeout(() => {
    window.open(targetHref, '_blank', 'noopener,noreferrer');
  }, 100);
}

function handleRepresentativeClick(event) {
  // Prevent the default navigation
  event.preventDefault();

  const targetHref = event.currentTarget.href;

  // Open in a new tab instead of changing the current location
  setTimeout(() => {
    window.open(targetHref, '_blank', 'noopener,noreferrer');
  }, 100);
}


  // Extract package type and level for current recommendation
  $: packageType = recommendation?.packageName
    ? recommendation.packageName.split(/[\s\/]/)[0]
    : "";
  $: level = recommendation?.packageName
    ? (packageType === "Premium" 
      ? "Premium"  // For Premium package, explicitly set level to "Premium"
      : recommendation.packageName
          .replace(packageType, "")
          .replace("Plan", "")
          .replace("Membership", "")
          .trim())
    : "";

    console.log('saste o level')
    console.log(level)
    console.log('saste o package type')
    console.log(packageType)
  
  // Elevated food & drink club surfaced when the fan opted in (Loudville → Gilly's,
  // Lower Bowl → Caesar's). Null otherwise.
  $: elevatedClub = getElevatedClub(
    $surveyStore["seating-3"] || $surveyStore.seating || $surveyStore.q3 ||
      $surveyStore["image-multi-choice-3"] || "",
    $surveyStore.elevatedExperience || $surveyStore["multi-choice-3.5"] ||
      $surveyStore["q3.5"] || ""
  );

  // Rocket Arena club page, attributed like every other outbound link here.
  $: elevatedClubUrl = elevatedClub
    ? appendAttribution(getElevatedClubUrl(elevatedClub), attributionSuffix)
    : null;

  $: contactRepRequired = isContactRepPackage(packageType);
  $: seatingImages = getSeatingImages(level);
  $: summaryText = getSummaryText(recommendation);
  $: galleryImageLink = contactRepRequired 
        ? representativeBookingUrl 
        : appendAttribution(getPackageLink(recommendation), attributionSuffix);
  $: selectedPerks = getSelectedPerks();
  $: organizedPerks = organizePerksWithTicketExchangeFirst(selectedPerks);

</script>

<div class="results-page">
  {#if loading}
    <div class="loading-container" in:fade={{ duration: 300 }}>
      <LoadingAnimation loadingDuration={LOADING_DURATION} />
      <p>Generating your personalized membership recommendation...</p>
    </div>
  {:else if error}
    <div class="error-container" in:fade={{ duration: 300 }}>
      <div class="error-icon">⚠️</div>
      <p>{error}</p>
      <div class="action-buttons">
        <button class="retry-button" on:click={handleRetakeSurvey}
          >Try <b>Again</b></button
        >
      </div>
    </div>
  {:else if recommendation}
    <div
      class="recommendation-container"
      in:fade={{ duration: 300, delay: 200 }}
    >
      <img
        src={packageType === "Premium" ? foundationLogo : unitedLogo}
        alt={packageType === "Premium" ? "Foundation Membership" : "Cavs United"}
        class="rec-logo"
        in:fade={{ duration: 300, delay: 200 }}
      />

      <h1 in:fly={{ y: -20, duration: 500, delay: 300 }}>
        Your Personalized Recommendation
      </h1>

      <!-- Location images above the text -->
      <div class="image-gallery" in:fly={{ y: 20, duration: 500, delay: 400 }}>
        {#each seatingImages as image, i}
          <a
            href={contactRepRequired ? representativeBookingUrl : config.ticketPackagesUrl}
            on:click={contactRepRequired ? handleRepresentativeClick : handleReserveClick}
            target="_blank"
            rel="noopener noreferrer"
            class="gallery-image {i > 0 ? 'hide-on-mobile' : ''}" 
            style="animation-delay: {i * 150}ms"
          >
            <img src={image} alt="Cavs {level} experience" />
          </a>
        {/each}
      </div>

      
      <div
        class="recommendation-card"
        class:foundation-theme={packageType === "Premium"}
        in:fly={{ y: 20, duration: 500, delay: 500 }}
      >
        {#if showEarlyBirdCountdown}
          <div class="early-bird-countdown">
            <span class="early-bird-label">Early Bird Ends in</span>
            <span class="early-bird-timer">{countdownText}</span>
          </div>
        {/if}


        {#if packageType === "Premium"}
          <div class="foundation-eyebrow">Foundation</div>
          <h2>Premium Membership</h2>
        {:else}
          <h2>{@html recommendation.packageName ? recommendation.packageName.replace(/\b[A-Z]+\b/g, '<b>$&</b>').replace(/\b\d+\b/g, '<b>$&</b>') : "Recommended Package"}</h2>
        {/if}
      
        <div class="recommendation-summary">
          <p>{@html summaryText}</p>
        </div>

        {#if elevatedClub}
          <div class="elevated-club-callout">
            <span class="elevated-club-label">Elevated Experience</span>
            <p>
              Enjoy all-inclusive food &amp; drink with access to
              {#if elevatedClubUrl}
                <a
                  href={elevatedClubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="elevated-club-link">{elevatedClub}</a>.
              {:else}
                <b>{elevatedClub}</b>.
              {/if}
            </p>
          </div>
        {/if}

        <div class="recommendation-details">
          <div class="detail-row">
            <div class="detail-label">Games</div>
            <div class="detail-value">
              {@html recommendation.details?.games ? recommendation.details.games.replace(/\b\d+\b/g, '<b>$&</b>').replace(/\b[A-Z]+\b/g, '<b>$&</b>') : "Multiple regular season games"}
            </div>
          </div>

          <div class="detail-row">
            <div class="detail-label">Pricing</div>
            <div class="detail-value">
              {#if config.stopMembershipSelling}
                2026–27 memberships will be available closer to the 2026-27 schedule release. You may place a deposit to secure a spot in the priority line and receive an early opportunity to select a membership once sales begin.
              {:else if packageType === "Premium"}
                Contact for pricing
              {:else if recommendation.details?.discountMonthlyPrice && recommendation.details?.discountSeatPrice}
                {@const monthlyPrice = showDiscountPricing 
                  ? recommendation.details.discountMonthlyPrice 
                  : recommendation.details.fullMonthlyPrice}

                {@const seatPrice = showDiscountPricing 
                  ? recommendation.details.discountSeatPrice 
                  : recommendation.details.fullSeatPrice}

                <div class="pricing-display">
                  <div class="monthly-pricing">
                    {#if showDiscountPricing && recommendation.details?.fullMonthlyPrice}
                      <span class="full-price">
                        ${recommendation.details.fullMonthlyPrice}
                      </span>
                    {/if}

                    <span class="current-price-number">
                      ${monthlyPrice}
                    </span>/mo per seat with 12 monthly payments and 0% interest*
                  </div>

                  <div class="seat-pricing">
                    Or from 

                    {#if showDiscountPricing && recommendation.details?.fullSeatPrice}
                      <span class="full-price">
                        ${recommendation.details.fullSeatPrice}
                      </span>
                    {/if}

                    <span class="current-price-number">
                      ${seatPrice}
                    </span> total per seat
                  </div>
                </div>
              {:else}
                Contact for pricing
              {/if}
            </div>
          </div>

          {#if recommendation.details?.savings}
            <div class="detail-row">
              <div class="detail-label">Savings</div>
              <div class="detail-value">{@html recommendation.details.savings.replace(/\d+%/g, '<b>$&</b>').replace(/\$[\d,\.]+/g, '<b>$&</b>').replace(/\b[A-Z]+\b/g, '<b>$&</b>').replace(/\b\d+\b/g, '<b>$&</b>')}</div>
            </div>
          {/if}

          <div class="detail-row">
            <div class="detail-label">Playoff Access</div>
            <div class="detail-value">
              {@html recommendation.details?.playoffAccess ? recommendation.details.playoffAccess.replace(/\b[A-Z]+\b/g, '<b>$&</b>').replace(/\b\d+\b/g, '<b>$&</b>').replace(/\d+%/g, '<b>$&</b>') : "Playoff ticket access available"}
            </div>
          </div>

        </div>
      
        <div class="action-buttons" in:fly={{ y: 20, duration: 500, delay: 600 }}>
          {#if packageType === "Premium"}
            <!-- Premium package gets special treatment -->
            <a
              href={premiumClubsUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="primary-button"
            >
              Explore Premium Clubs
            </a>
            
            <a
              href={representativeBookingUrl}
              on:click={handleRepresentativeClick}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button"
            >
              Contact Us To Discuss Seating And Pricing
            </a>
            
            <!-- Phone Call button - only shown during business hours -->
            {#if showCallButton}
              <a
                href={formattedPhone.dialing}
                on:click={handlePhoneCallClick}
                class="call-button"
              >
                <span class="call-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                  </svg>
                </span>
                <span class="call-text">
                  <span class="call-primary-text">Speak with a Representative Now</span>
                  <span class="call-phone-number">{formattedPhone.display}</span>
                </span>
              </a>
              
            {/if}
            
          {:else if packageType === "Full"}
            <!-- Full package with consistent CTAs -->
            <a
              href={fullMembershipUrl}
              on:click={handleReserveClick}
              target="_blank"
              rel="noopener noreferrer"
              class="primary-button"
            >
              Explore Availability and Pricing
            </a>
            
            <a
              href={representativeBookingUrl}
              on:click={handleRepresentativeClick}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button"
            >
              Book time with Representative
            </a>
            
            <!-- Phone Call button - only shown during business hours -->
            {#if showCallButton}
              <a
                href={formattedPhone.dialing}
                on:click={handlePhoneCallClick}
                class="call-button"
              >
                <span class="call-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                  </svg>
                </span>
                <span class="call-text">
                  <span class="call-primary-text">Speak with a Representative Now</span>
                  <span class="call-phone-number">{formattedPhone.display}</span>
                </span>
              </a>
            {/if}
            
            <a
              href={attributedFaqUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button faq-button"
            >
              Membership FAQ's
            </a>
          {:else if contactRepRequired}
            <!-- Other contact required packages -->
            <a
              href={representativeBookingUrl}
              on:click={handleRepresentativeClick}
              target="_blank"
              rel="noopener noreferrer"
              class="primary-button"
            >
              Contact Us To Discuss Seating And Pricing
            </a>
            
            <!-- Phone Call button - only shown during business hours -->
            {#if showCallButton}
              <a
                href={formattedPhone.dialing}
                on:click={handlePhoneCallClick}
                class="call-button"
              >
                <span class="call-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                  </svg>
                </span>
                <span class="call-text">
                  <span class="call-primary-text">Speak with a Representative Now</span>
                  <span class="call-phone-number">{formattedPhone.display}</span>
                </span>
              </a>
            {/if}
            
            <a
              href={attributedFaqUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button"
            >
              Membership FAQ's
            </a>
          {:else}
            <!-- Quarter and Half packages use SeatGeek links -->
            <a
              href={config.ticketPackagesUrl}
              on:click={handleReserveClick}
              target="_blank"
              rel="noopener noreferrer"
              class="primary-button"
            >
            Explore Availability and Pricing
            </a>
        
            <a
              href={representativeBookingUrl}
              on:click={handleRepresentativeClick}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button"
            >
              Book time with Representative
            </a>
            
            <!-- Phone Call button - only shown during business hours -->
            {#if showCallButton}
              <a
                href={formattedPhone.dialing}
                on:click={handlePhoneCallClick}
                class="call-button"
              >
                <span class="call-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                  </svg>
                </span>
                <span class="call-text">
                  <span class="call-primary-text">Speak with a Representative Now</span>
                  <span class="call-phone-number">{formattedPhone.display}</span>
                </span>
              </a>
              <div class="hours-note">Available until 5:30 PM EST today</div>
            {/if}
            
            <a
              href={attributedFaqUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button faq-button"
            >
              Membership FAQ's
            </a>
          {/if}
        </div>
      
          <div class="perks-section">
            <h3>Top benefits within membership</h3>
            <div class="perks-grid">
              {#each organizedPerks as perkId, index}
                {@const perkInfo = getPerkInfo(perkId)}
                <div
                  class="perk-item"
                  in:fade={{
                    duration: 300,
                    delay: 600 + index * 100,
                  }}
                >
                  <div class="perk-image">
                    <a href={attributedBenefitsUrl} target="_blank" rel="noopener noreferrer">
                      <img src={perkInfo.image} alt={perkInfo.label} />
                    </a>
                  </div>
                  <div class="perk-label">{@html perkInfo.label.replace(/\b[A-Z]+\b/g, '<b>$&</b>').replace(/\b\d+\b/g, '<b>$&</b>')}</div>
                </div>
              {/each}
            </div>
          </div>
          <a 
            href={attributedBenefitsUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            class="benefits-link"
            in:fade={{ duration: 300, delay: 800 }}
          >
            Explore all benefits →
          </a>
      </div> <!-- This closes recommendation-card -->
      
      <div class="action-buttons" in:fly={{ y: 20, duration: 500, delay: 600 }}>
       
        <a
            href={attributedFaqUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="tertiary-button"
          >          Explore Membership Packages
        </a>
        
      </div>
      
      <div class="success-message" in:fade={{ duration: 300, delay: 700 }}>
        <p>
          Your results have been sent to your email. Check your inbox for
          additional information and next steps!
        </p>
      </div>
    </div>
  {:else}
    <!-- Fallback for unexpected state where neither loading, error nor recommendation are set -->
    <div class="error-container" in:fade={{ duration: 300 }}>
      <div class="error-icon">⚠️</div>
      <p>An unexpected error occurred. Please try again.</p>
      <div class="action-buttons">
        <button class="retry-button" on:click={handleRetakeSurvey}
          >Try <b>Again</b></button
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .results-page {
    width: 100%;
    max-width: none; /* Remove max-width constraint */
    margin: 0 auto;
    padding: 2rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.9);
      opacity: 0.8;
    }
    100% {
      transform: scale(1.1);
      opacity: 1;
    }
  }

  .error-container {
    text-align: center;
    padding: 2rem;
  }

  .error-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  .sold-out-notice {
    background-color: #f8f4e5;
    border-left: 4px solid #f0ad4e;
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    border-radius: 4px;
  }
  
  .sold-out-notice p {
    margin: 0;
    color: #856404;
    font-weight: 500;
  }
  
  .sold-out-notice a {
    color: #6A0032;
    text-decoration: underline;
    font-weight: 600;
  }
  
  .sold-out-notice a:hover {
    text-decoration: none;
  }

  .retry-button {
    background-color: #6a0032;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 30px;
    font-size: 1rem;
    cursor: pointer;
    margin-top: 1.5rem;
  }

  .recommendation-container {
    text-align: center;
    width: 100%;
    max-width: none; /* Remove container constraints */
  }

  h1 {
    font-size: 2.5rem;
    color: #6a0032;
    margin-bottom: 2rem;
  }

  /* Full-width image gallery */
  .image-gallery {
    display: flex !important;
    flex-direction: row !important; 
    justify-content: center !important;
    align-items: center !important;
    gap: 15px !important;
    width: 100% !important;
    margin-bottom: 2.5rem !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    max-width: none !important;
    overflow: visible !important;
  }

  .gallery-image {
    width: 300px !important; /* Fixed width */
    height: 200px !important; /* Fixed height */
    flex: 0 0 auto !important;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    opacity: 0;
    animation: fadeIn 0.5s forwards;
    display: block; /* Ensure it's a block-level element */
    text-decoration: none; /* Remove default link styling */
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .gallery-image:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  .gallery-image img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }

  .recommendation-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    margin-bottom: 2rem;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  /* Recommendation header logo (Cavs United shield / Foundation mark) */
  .rec-logo {
    display: block;
    max-width: 130px;
    height: auto;
    margin: 0 auto 1.25rem;
  }

  /* Foundation (Premium) branding — black & gold */
  .foundation-eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 700;
    font-size: 0.85rem;
    color: #BB945C; /* Cavs gold */
    margin-bottom: 0.25rem;
  }

  .recommendation-card.foundation-theme {
    border: 2px solid #BB945C;
    background-color: #ffffff;
  }

  .foundation-theme .primary-button {
    background-color: #111111;
    color: #BB945C;
  }

  .foundation-theme .primary-button:hover {
    background-color: #000000;
  }

  .foundation-theme .secondary-button {
    color: #111111;
    border-color: #111111;
  }

  .foundation-theme .secondary-button:hover {
    background-color: rgba(17, 17, 17, 0.05);
  }

  .foundation-theme .benefits-link {
    color: #111111;
  }

  h2 {
    font-size: 1.8rem;
    color: #333;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  /* New styles for summary section */
  .recommendation-summary {
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #eee;
    padding-bottom: 1rem;
  }

  .recommendation-summary p {
    font-size: 1.1rem;
    line-height: 1.5;
    color: #555;
  }

  .elevated-club-callout {
    background: linear-gradient(135deg, #1a1a1a 0%, #2b2b2b 100%);
    border: 1px solid #BB945C;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
  }

  .elevated-club-label {
    display: block;
    color: #BB945C; /* Cavs gold */
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
  }

  .elevated-club-callout p {
    margin: 0;
    color: #fff;
    font-size: 1.05rem;
    line-height: 1.4;
  }

  .elevated-club-link {
    color: #BB945C; /* Cavs gold */
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .elevated-club-link:hover {
    color: #d4ad70;
  }

  .elevated-club-callout b {
    color: #BB945C;
  }

  .recommendation-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1.5rem;
  }

  .detail-row {
    display: flex;
    text-align: left;
    border-bottom: 1px solid #eee;
    padding-bottom: 1rem;
  }

  .detail-label {
    flex: 0 0 30%;
    font-weight: 600;
    color: #666;
  }

  .detail-value {
    flex: 1;
    color: #333;
  }

  /* Phone call button styling */
  .call-button {
    display: flex;
    align-items: center;
    background-color: #00b359; /* Green color for call buttons */
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 30px;
    text-decoration: none;
    margin-top: 1rem;
    transition: all 0.2s ease;
    animation: pulse-green 2s infinite;
    box-shadow: 0 4px 8px rgba(0, 179, 89, 0.3);
  }
  
  @keyframes pulse-green {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 179, 89, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 179, 89, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 179, 89, 0);
    }
  }
  
  .call-button:hover {
    background-color: #009e4f;
    transform: translateY(-2px);
  }
  
  .call-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.75rem;
  }
  
  .call-text {
    display: flex;
    flex-direction: column;
  }
  
  .call-primary-text {
    font-weight: 600;
    font-size: 1rem;
  }
  
  .call-phone-number {
    font-size: 0.85rem;
    opacity: 0.9;
  }
  
  .hours-note {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.5rem;
    font-style: italic;
  }

  /* New styles for perks section */
  .perks-section {
    margin-top: 2rem;
  }

  .perks-section h3 {
    font-size: 1.4rem;
    color: #333;
    margin-bottom: 1.5rem;
    font-weight: 600;
    text-align: center;
  }

  .perks-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;
  }

  .perk-item {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .perk-image {
    width: 100%;
    height: 120px;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 0.75rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .perk-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .perk-label {
    font-size: 0.9rem;
    color: #555;
    text-align: center;
    line-height: 1.3;
  }
  
  .benefits-link {
    display: inline-block;
    margin-top: 1.5rem;
    color: #6a0032;
    font-weight: 600;
    text-decoration: none;
    font-size: 1rem;
    transition: all 0.2s ease;
  }
  
  .benefits-link:hover {
    text-decoration: underline;
    transform: translateX(3px);
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 800px;
    margin-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
  }

  .primary-button {
    background-color: #6a0032;
    color: white;
    border: none;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-decoration: none;
    display: inline-block;
    text-align: center;
  }

  .primary-button:hover {
    background-color: #6d0030;
  }

  .secondary-button {
    background-color: white;
    color: #6a0032;
    border: 2px solid #6a0032;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    display: inline-block;
    text-align: center;
  }

  .secondary-button:hover {
    background-color: rgba(134, 0, 56, 0.05);
  }

  .success-message {
    background-color: #e8f5e9;
    border-radius: 8px;
    padding: 1rem;
    color: #2e7d32;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  .tertiary-button {
    background-color: white;
    color: #333;
    border: 1px solid #ccc;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 1rem; /* Add spacing from other buttons */
    width: 100%; /* Make it full width for better separation */
  }

  .tertiary-button:hover {
    background-color: #f5f5f5;
    color: #6a0032; /* Cavs wine color on hover */
    border-color: #6a0032;
  }

  /* Smaller screens - maintain horizontal layout */
  @media (max-width: 768px) {
    .results-page {
      padding: 1rem;
    }

    .hide-on-mobile {
    display: none !important;
  }
    
  .image-gallery {
    justify-content: center !important;
  }

    /* Make the single visible image bigger on mobile */
  .gallery-image {
    width: 85% !important;
    max-width: 350px !important;
    height: auto !important;
    aspect-ratio: 3/2; /* Maintain aspect ratio */
  }

    .primary-button,
    .secondary-button,
    .tertiary-button {
      flex: 0 1 auto;
    }

    .action-buttons {
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: center;
    }
    
    /* Mobile call button adjustments */
    .call-button {
      flex-direction: column;
      padding: 1rem;
    }
    
    .call-icon {
      margin-right: 0;
      margin-bottom: 0.5rem;
    }
    
    .call-text {
      text-align: center;
    }
    
    .call-primary-text {
      margin-bottom: 0.25rem;
    }

    h1 {
      font-size: 2rem;
    }

    h2 {
      font-size: 1.5rem;
    }

    .detail-row {
      flex-direction: column;
    }

    .detail-label {
      margin-bottom: 0.5rem;
    }

    .perks-grid {
      grid-template-columns: 1fr; /* Stack items vertically */
      max-width: 250px; /* More compact width */
      margin-left: auto;
      margin-right: auto;
      gap: 1rem; /* Reduced gap between items */
    }
    
    .perk-item {
      margin-bottom: 0.75rem; /* Reduced spacing between items */
    }
    
    .perk-image {
      height: 110px; /* Smaller images on mobile */
    }
    
    .perk-label {
      font-size: 0.9rem; /* Slightly smaller text */
      margin-top: 0.25rem; /* Less space between image and text */
    }
    
    .perks-section h3 {
      font-size: 1.2rem; /* Smaller heading */
      margin-bottom: 1rem; /* Less space below heading */
    }
    
    /* Optional: Adjust the "Explore all benefits" link */
    .benefits-link {
      margin-top: 0.75rem;
      font-size: 0.9rem;
    }
  }

    .early-bird-countdown {
      text-align: center;
      margin-bottom: 0.75rem;
    }

    .early-bird-label {
      font-size: 1.05rem;   /* larger */
      font-weight: 700;     /* bold */
      color: #444;
      margin-bottom: 0.25rem;
      display: block;
    }

    .early-bird-timer {
      font-size: 1.75rem;
      font-weight: 700;
      color: #6a0032; /* 🎯 countdown color */
    }

    /* Pricing section */
    .recommendation-details .detail-value {
      font-size: 1rem; /* base size, matches other detail text */
      color: #000;     /* default text color */
    }

    .recommendation-details .detail-value .full-price {
      color: red;
      text-decoration: line-through;
      margin-right: 0.25rem;
    }

    .recommendation-details .detail-value .current-price-number {
      font-weight: bold;
    }
    /* Pricing display styles */
    .pricing-display {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .monthly-pricing,
    .seat-pricing {
      line-height: 1.5;
    }

    .full-price {
      text-decoration: line-through;
      color: #888;
      margin-right: 0.25rem;
      font-size: 1.1 em;
      font-weight: bold;
    }

    .current-price-number {
      font-weight: bold;
      color: #000; /* Highlight new price */
      font-size: 1.1em;
    }

    /* Mobile responsiveness for pricing */
    @media (max-width: 768px) {
      .pricing-display {
        font-size: 0.95rem;
      }
      
      .monthly-pricing,
      .seat-pricing {
        line-height: 1.4;
      }
    }



  /* Very small screens - stack images vertically */
  @media (max-width: 480px) {
    .image-gallery {
      flex-direction: column !important;
      align-items: center !important;
    }
    
    .gallery-image {
      width: 100% !important;
      max-width: 350px !important;
      height: auto !important;
      aspect-ratio: 3/2;
    }
  }
</style>
